/**
 * Save authorization: C0's invariants on top of round B's draft revision (Option C, plan §3.1).
 *
 * Round B (`useDraftRevision`): Save and Save anyway capture the draft revision before their first await and re-check
 * it right before persisting; any value edit moves it. C0 adds the draft operations as invalidation sources. An engine
 * select, a restore, a patch (paste, prefill, Browse Azure) and a dismissal each call `invalidatePendingWork`
 * synchronously, BEFORE the form is captured or rewritten:
 * - a fix retry's still-owned change (e.g. trust-server-certificate) is reverted, so a draft never captures it;
 * - the revision moves, so a pending Save or Save anyway saves nothing — even after A → B → A with identical values;
 * - the Test and Load tokens move and the watchdog clears, so a pending Test or Load shows nothing;
 * - the Save stops counting as in flight, and an engine change starts a new result generation.
 *
 * While a Save is in flight, the entry points (paste, Detect, Import, Browse Azure) are unavailable with the reason
 * "Saving…": a Save that completes closes the dialog, which must never happen under an open picker. A running Test
 * does not disable them, and Cancel always stays enabled.
 */
import { useMemo, useRef, useState } from 'react';

export const SAVING_REASON = 'Saving…';

export type PendingWork = {
  abandonFix: () => void;
  revokeSaves: () => void;
  abortTest: () => void;
  abortLoad: () => void;
  endSaving: () => void;
  newGeneration: () => void;
};

export function invalidatePendingWork(work: PendingWork, change: { engineChanged: boolean }): void {
  work.abandonFix();
  work.revokeSaves();
  work.abortTest();
  work.abortLoad();
  work.endSaving();
  if (change.engineChanged) work.newGeneration();
}

/** A Save or Save anyway from its click until it saved, refused, or was voided by an invalidation. */
export function useSaveInFlight() {
  const [token, setToken] = useState<number | null>(null);
  const counter = useRef(0);
  const current = useRef<number | null>(null);
  const controls = useMemo(() => ({
    begin: () => {
      counter.current += 1;
      current.current = counter.current;
      setToken(counter.current);
      return counter.current;
    },
    end: (started: number) => {
      if (current.current !== started) return;
      current.current = null;
      setToken(null);
    },
    clear: () => {
      current.current = null;
      setToken(null);
    },
  }), []);
  return { saving: token !== null, ...controls };
}
